/**
 * Shared helpers for agenda items.
 *
 * `stableAgendaItemId` MUST match the backend `_stable_item_id` in
 * routes/event_agenda.py so a user's starred sessions survive minor edits
 * (and so backend reminder dispatch targets the exact row the user starred).
 */
import md5 from 'blueimp-md5';

export function stableAgendaItemId(item) {
  if (!item) return '';
  if (item.id) return String(item.id);
  const key = `${item.title || ''}|${item.time || ''}|${item.room || ''}`;
  return md5(key).slice(0, 12);
}

/**
 * Parse an agenda `time` string (e.g. "9:45 - 10:00" or "10:30") into
 * { start, end } minute offsets from midnight. Returns null if unparseable.
 */
export function parseAgendaTime(timeStr) {
  if (!timeStr) return null;
  const parts = String(timeStr).split(/[-–—]/).map(s => s.trim());
  const toMin = (s) => {
    const m = s.match(/^(\d{1,2})[:.](\d{2})$/);
    if (!m) return null;
    const h = parseInt(m[1], 10);
    const mm = parseInt(m[2], 10);
    if (h > 23 || mm > 59) return null;
    return h * 60 + mm;
  };
  const start = toMin(parts[0]);
  if (start == null) return null;
  const end = parts[1] ? toMin(parts[1]) : start + 30;
  return { start, end: end ?? start + 30 };
}

/**
 * Given the user's starred agenda items (list of items with time), find
 * clashes. Returns a Map item_id -> array of clashing titles.
 */
export function computeClashes(items) {
  const map = new Map();
  const parsed = items
    .map(it => ({ it, span: parseAgendaTime(it.time) }))
    .filter(x => x.span);
  for (let i = 0; i < parsed.length; i++) {
    for (let j = i + 1; j < parsed.length; j++) {
      const a = parsed[i], b = parsed[j];
      const overlap = a.span.start < b.span.end && b.span.start < a.span.end;
      if (!overlap) continue;
      const idA = stableAgendaItemId(a.it);
      const idB = stableAgendaItemId(b.it);
      map.set(idA, [...(map.get(idA) || []), b.it.title || 'another session']);
      map.set(idB, [...(map.get(idB) || []), a.it.title || 'another session']);
    }
  }
  return map;
}
